/**
 * AdminPage
 * Kitchly v0.5.5
 *
 * Admin dashboard: manage invite codes, view system stats.
 * Only accessible to users with is_admin = true.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Ticket,
  Plus,
  Users,
  Package,
  ShoppingCart,
  BarChart3,
  Copy,
  ToggleLeft,
  ToggleRight,
  RefreshCw
} from 'lucide-react';
import { admin } from '../services/api';

function AdminPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCode, setNewCode] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [codesRes, statsRes] = await Promise.all([
        admin.inviteCodes(),
        admin.stats()
      ]);
      setCodes(codesRes.codes || []);
      setStats(statsRes.stats || null);
    } catch (err) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateCode = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const data = newCode.trim() ? { code: newCode.trim() } : {};
      const result = await admin.createCode(data);
      toast.success(t('admin.codeCreated', { code: result.code }));
      setNewCode('');
      setShowCreateForm(false);
      loadData();
    } catch (err) {
      toast.error(err.data?.error || t('common.error'));
    } finally {
      setCreating(false);
    }
  };

  const handleToggleCode = async (id, currentActive) => {
    try {
      await admin.toggleCode(id, !currentActive);
      toast.success(currentActive ? t('admin.codeDeactivated') : t('admin.codeActivated'));
      setCodes(prev => prev.map(c => c.id === id ? { ...c, is_active: !currentActive } : c));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      toast.success(t('admin.codeCopied', { code }));
    }).catch(() => {
      toast.error('Copy failed — try manually');
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="admin-page" style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: 'var(--space-md)', gap: 'var(--space-md)' }}>
        <button className="btn-icon" onClick={() => navigate('/settings')}>
          <ArrowLeft size={24} />
        </button>
        <h1 style={{ flex: 1, margin: 0 }}>{t('admin.title')}</h1>
        <button className="btn-icon" onClick={loadData} disabled={loading}>
          <RefreshCw size={20} className={loading ? 'spin' : ''} />
        </button>
      </div>

      <div style={{ padding: '0 var(--space-md)' }}>

        {/* === SYSTEM STATS === */}
        {stats && (
          <div className="card mb-lg">
            <h3 className="card-title mb-md" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <BarChart3 size={18} /> {t('admin.stats')}
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 'var(--space-sm)'
            }}>
              {[
                { label: t('admin.statUsers'), value: stats.total_users, icon: Users, color: '#7A9E7E' },
                { label: t('admin.statGroups'), value: stats.total_groups, icon: Users, color: '#7EB8D4' },
                { label: t('admin.statItems'), value: stats.total_inventory_items, icon: Package, color: '#C4956A' },
                { label: t('admin.statProducts'), value: stats.total_products, icon: Package, color: '#9B8EC4' },
                { label: t('admin.statShopping'), value: stats.active_shopping_items, icon: ShoppingCart, color: '#D9534F' },
                { label: t('admin.statCodes'), value: stats.active_invite_codes, icon: Ticket, color: '#4CAF50' }
              ].map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <div key={i} style={{
                    background: stat.color + '11',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-sm)',
                    textAlign: 'center'
                  }}>
                    <Icon size={18} color={stat.color} style={{ marginBottom: 4 }} />
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: stat.color, fontFamily: 'var(--font-heading)' }}>
                      {stat.value}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                      {stat.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* === INVITE CODES === */}
        <div className="card mb-lg">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-md)' }}>
            <h3 className="card-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
              <Ticket size={18} /> {t('admin.inviteCodes')}
            </h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowCreateForm(!showCreateForm)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', fontSize: '13px' }}
            >
              <Plus size={16} />
              {t('admin.newCode')}
            </button>
          </div>

          {/* Create Code Form */}
          {showCreateForm && (
            <form onSubmit={handleCreateCode} style={{
              background: 'var(--color-bg-input)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-md)',
              marginBottom: 'var(--space-md)'
            }}>
              <div className="form-group" style={{ marginBottom: 'var(--space-sm)' }}>
                <label className="form-label">{t('admin.customCode')}</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder={t('admin.customCodePlaceholder')}
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                  maxLength={50}
                  style={{ textTransform: 'uppercase' }}
                />
                <p className="text-xs text-secondary mt-sm">{t('admin.autoGenNote')}</p>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={creating}>
                  {creating ? t('common.loading') : t('admin.createCode')}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setShowCreateForm(false); setNewCode(''); }}>
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          )}

          {/* Codes List */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-lg)' }}>
              <div className="loader-spinner" style={{ margin: '0 auto' }} />
            </div>
          ) : codes.length === 0 ? (
            <p style={{ color: 'var(--color-text-light)', textAlign: 'center', padding: 'var(--space-md)' }}>
              {t('admin.noCodes')}
            </p>
          ) : (
            codes.map(code => (
              <div key={code.id} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)',
                padding: 'var(--space-sm) 0',
                borderBottom: '1px solid var(--color-border)'
              }}>
                {/* Status indicator */}
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: code.is_active ? '#4CAF50' : '#D9534F',
                  flexShrink: 0
                }} />

                {/* Code details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 600,
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    letterSpacing: '0.5px',
                    opacity: code.is_active ? 1 : 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {code.code}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                    {t('admin.usedCount', { count: code.times_used })} &middot; {formatDate(code.created_at)}
                    {code.created_by_name && ` &middot; ${code.created_by_name}`}
                  </div>
                </div>

                {/* Actions */}
                <button
                  className="btn-icon"
                  onClick={() => handleCopyCode(code.code)}
                  style={{ padding: 6, color: 'var(--color-text-secondary)' }}
                  title="Copy code"
                >
                  <Copy size={16} />
                </button>
                <button
                  className="btn-icon"
                  onClick={() => handleToggleCode(code.id, code.is_active)}
                  style={{ padding: 6, color: code.is_active ? '#4CAF50' : '#D9534F' }}
                  title={code.is_active ? 'Deactivate' : 'Activate'}
                >
                  {code.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
